import { CurrentIdentityProvider } from '@pl/authorisation-client';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as _ from 'lodash';
import * as i1 from 'angular-oauth2-oidc';

class AngularOidcCurrentIdentityProvider extends CurrentIdentityProvider {
    static { this.ACTOR_PARAMETER_NAME = "act"; }
    static { this.ACTOR_SESSION_STORAGE_KEY = "imp_actor_session"; }
    static { this.sessionItemKeys = ["refresh_token",
        "nonce",
        "expires_at",
        "id_token_expires_at",
        "access_token_stored_at",
        "id_token_stored_at",
        "id_token",
        "access_token",
        "session_state",
        "id_token_claims_obj",
        "granted_scopes"]; }
    constructor(oauthStorage, oAuthService) {
        super();
        this.oauthStorage = oauthStorage;
        this.oAuthService = oAuthService;
        this.isImpersonating$ = new BehaviorSubject(false);
        this.isImpersonating$.next(this.isImpersonating());
    }
    get actingIdentityId() {
        return this.tokenDetails?.actor?.identityId || this.currentIdentityId;
    }
    get currentIdentityId() {
        return this.tokenDetails?.identityId;
    }
    get identityProfileDetails() {
        return (async () => {
            if (!this.userProfile) {
                const profileFromService = (await this.oAuthService.loadUserProfile());
                this.userProfile = (profileFromService.info || profileFromService);
            }
            return this.convertPLASMeResponseToIdentityProfileDetails(this.userProfile);
        })();
    }
    get tokenDetails() {
        const a = this.oAuthService.getIdentityClaims();
        return a && this.convertPLASTokenResponseToIdentityTokenDetails(a, this.oAuthService.getAccessToken());
    }
    async endImpersonationSession() {
        if (!this.isImpersonating()) {
            throw new Error("Impersonation session not active. Exiting.");
        }
        AngularOidcCurrentIdentityProvider.sessionItemKeys.map(e => this.oauthStorage.removeItem(e));
        const oldUserSessionState = this.oauthStorage.getItem(AngularOidcCurrentIdentityProvider.ACTOR_SESSION_STORAGE_KEY);
        this.oauthStorage.removeItem(AngularOidcCurrentIdentityProvider.ACTOR_SESSION_STORAGE_KEY);
        this.oauthStorage.removeItem(AngularOidcCurrentIdentityProvider.ACTOR_PARAMETER_NAME);
        if (oldUserSessionState) {
            // tslint:disable-next-line:no-console
            console.warn("No old session details. Will just clear impersonation session items and reload.");
            const c = JSON.parse(oldUserSessionState);
            AngularOidcCurrentIdentityProvider.sessionItemKeys.map(e => this.oauthStorage.setItem(e, c[e] || ""));
        }
        window.location.reload();
        return undefined;
    }
    async impersonate(identityId, reason) {
        let result = undefined;
        if (!this.appNameOrId) {
            throw new Error("Application Name or Id not provided for AngularOidcCurrentIdentityProvider. Please call AngularOidcCurrentIdentityProvider.configure method in your app initializer.");
        }
        await this.validateImpersonation(identityId, reason);
        const currentUserToken = this.oAuthService.getAccessToken();
        const currentUserSession = _.chain(AngularOidcCurrentIdentityProvider.sessionItemKeys)
            .keyBy(e => e)
            .mapValues(e => this.oauthStorage.getItem(e))
            .value();
        try {
            AngularOidcCurrentIdentityProvider.sessionItemKeys.map(e => this.oauthStorage.removeItem(e));
            const newNonce = await this.oAuthService.createAndSaveNonce();
            const r = await this.oAuthService.fetchTokenUsingGrant("user_impersonation", {
                sub: identityId,
                actor_token: currentUserToken,
                nonce: newNonce,
                audience: this.appNameOrId,
                reason
            });
            this.oauthStorage.setItem(AngularOidcCurrentIdentityProvider.ACTOR_SESSION_STORAGE_KEY, JSON.stringify(currentUserSession));
            result = this.tokenDetails;
        }
        catch (e) {
            AngularOidcCurrentIdentityProvider.sessionItemKeys.map(e => this.oauthStorage.setItem(e, currentUserSession[e] || ""));
            throw new Error("Error while starting impersonation. Details: " + JSON.stringify(e));
        }
        window.location.reload();
        return result;
    }
    isImpersonating() {
        return !!this.tokenDetails?.actor || !!this.oauthStorage.getItem(AngularOidcCurrentIdentityProvider.ACTOR_PARAMETER_NAME);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AngularOidcCurrentIdentityProvider, deps: [{ token: i1.OAuthStorage }, { token: i1.OAuthService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AngularOidcCurrentIdentityProvider }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AngularOidcCurrentIdentityProvider, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.OAuthStorage }, { type: i1.OAuthService }]; } });

class AuthorisationClientAngularOauth2OidcModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorisationClientAngularOauth2OidcModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AuthorisationClientAngularOauth2OidcModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorisationClientAngularOauth2OidcModule, providers: [
            AngularOidcCurrentIdentityProvider,
            { provide: CurrentIdentityProvider, useClass: AngularOidcCurrentIdentityProvider }
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthorisationClientAngularOauth2OidcModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                    providers: [
                        AngularOidcCurrentIdentityProvider,
                        { provide: CurrentIdentityProvider, useClass: AngularOidcCurrentIdentityProvider }
                    ]
                }]
        }] });

/*
 * Public API Surface of authorisation-client-angular-oauth2-oidc
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AngularOidcCurrentIdentityProvider, AuthorisationClientAngularOauth2OidcModule };
